<template>
  <div
    v-if="visibleBanners.length > 0"
    class="reward-banner-container"
  >
    <div
      v-for="banner in visibleBanners"
      :key="`reward_banner_${banner.promotionId}`"
      class="reward-banner d-flex align-items-center p-2 mb-2 rounded"
    >
      <b-icon
        icon="gift-fill"
        font-scale="1.5"
        class="text-success mr-2 flex-shrink-0"
      />
      <div class="flex-grow-1">
        <p class="mb-0 font-weight-bold">
          {{ banner.title }}
        </p>
        <p class="mb-0 small text-muted">
          {{ banner.subtitle }}
        </p>
      </div>
      <template v-if="banner.description">
        <button
          :id="`reward_banner_info_${banner.promotionId}`"
          type="button"
          class="reward-banner-info-btn flex-shrink-0 ml-2"
          :aria-label="$t('Detalhes da promoção')"
        >
          <b-icon
            icon="info-circle"
            font-scale="1.5"
            class="text-success"
          />
        </button>
        <b-popover
          :target="`reward_banner_info_${banner.promotionId}`"
          triggers="click blur"
          placement="left"
          :title="banner.name || $t('Promoção')"
        >
          <div class="reward-banner-description">
            {{ banner.description }}
          </div>
        </b-popover>
      </template>
    </div>
  </div>
</template>

<script>
import { BIcon, BPopover } from 'bootstrap-vue'
import { mapGetters, mapState } from 'vuex'
import Decimal from 'decimal.js-light'
import { spendAndGetUtils } from '@/mixins'

export default {
  components: { BIcon, BPopover },

  computed: {
    ...mapGetters('pages/pdv/payBox', ['getItems']),
    ...mapGetters('pages/pdv/effectivePriceTable', ['effectivePriceTable']),
    ...mapState('pages/pdv/spendAndGetPromotions', ['activePromotions']),
    ...mapGetters('pages/pdv/spendAndGetPromotions', ['activePromotionIds']),

    /**
     * A venda é elegível para banners de SpendAndGet quando a tabela de preço
     * EFETIVA tem `activeOnline = true`. O getter `effectivePriceTable` já
     * aplica a regra de fallback para a default quando a tabela do cliente
     * não pode reger o canal (ATM com activeAtm=false, tabela inativa).
     *
     * Se a tabela efetiva ainda não foi resolvida (default não carregada e
     * sem tabela de venda), deixamos passar (bootstrap inicial); se resolvida
     * e não-Online, ocultamos os banners.
     */
    saleIsEligible() {
      if (!this.effectivePriceTable) return true
      return this.effectivePriceTable.activeOnline === true
    },

    /**
     * Para cada promoção SpendAndGet ativa, calcula:
     * - Se já atingiu uma tier com pelo menos um brinde com estoque: mostra "Você ganhou X"
     *   (e, se houver tier maior, mostra também "Faltam R$Y para um brinde ainda melhor")
     * - Se ainda não atingiu nenhuma tier com brinde disponível: mostra "Faltam R$Y para o próximo brinde"
     * - Se não há tier com brinde disponível em nenhum dos níveis: oculta o banner
     */
    visibleBanners() {
      if (!this.saleIsEligible) return []
      if (!this.activePromotions || this.activePromotions.length === 0) return []

      const eligibleAmount = spendAndGetUtils.calculateEligibleAmount(
        this.getItems,
        this.activePromotionIds
      )

      const tierHasAvailableReward = tier => {
        if (!tier?.rewards?.length) return false
        return tier.rewards.some(r => {
          if (!r.sku || r.sku.price == null) return false
          const available = Number(r.sku.stock ?? 0)
          return available >= Number(r.quantity)
        })
      }

      return this.activePromotions
        .map(promotion => {
          const sortedTiers = [...(promotion.tiers || [])].sort(
            (a, b) => Number(a.minimumAmount) - Number(b.minimumAmount)
          )
          if (sortedTiers.length === 0) return null

          // Considera "disponível" apenas a tier que tem ao menos um brinde
          // com estoque suficiente para a quantidade configurada.
          const availableTiers = sortedTiers.filter(tierHasAvailableReward)
          if (availableTiers.length === 0) return null

          const appliedTier = availableTiers
            .filter(t => Number(t.minimumAmount) <= eligibleAmount)
            .reduce(
              (max, t) => (max === null || Number(t.minimumAmount) > Number(max.minimumAmount) ? t : max),
              null
            )
          const nextTier = availableTiers.find(
            t => Number(t.minimumAmount) > eligibleAmount
          )

          const formatCurrency = value => this.$options.filters.currency(value, null, 2)

          if (appliedTier && !nextTier) {
            return {
              promotionId: promotion.id,
              title: this.$t('Você ganhou um brinde!'),
              subtitle: `${promotion.name || ''}`.trim(),
              name: promotion.name || null,
              description: promotion.description || null,
            }
          }

          if (appliedTier && nextTier) {
            const remaining = new Decimal(nextTier.minimumAmount).minus(eligibleAmount).toNumber()
            return {
              promotionId: promotion.id,
              title: this.$t('Você ganhou um brinde!'),
              subtitle: `Faltam ${formatCurrency(remaining)} para um brinde ainda melhor`,
              name: promotion.name || null,
              description: promotion.description || null,
            }
          }

          if (!appliedTier && nextTier) {
            const remaining = new Decimal(nextTier.minimumAmount).minus(eligibleAmount).toNumber()
            return {
              promotionId: promotion.id,
              title: `${promotion.name || this.$t('Promoção')}`,
              subtitle: `Faltam ${formatCurrency(remaining)} para ganhar um brinde`,
              name: promotion.name || null,
              description: promotion.description || null,
            }
          }

          return null
        })
        .filter(b => b !== null)
    },
  },
}
</script>

<style lang="scss" scoped>
.reward-banner {
  background: linear-gradient(90deg, rgba(40, 199, 111, 0.08), rgba(40, 199, 111, 0.02));
  border: 1px solid rgba(40, 199, 111, 0.25);
}

.reward-banner-info-btn {
  background: transparent;
  border: none;
  padding: 4px 6px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.06);
    outline: none;
  }
}
</style>

<style lang="scss">
.reward-banner-description {
  white-space: pre-line;
  max-width: 320px;
  font-size: 0.9rem;
}
</style>
